import { Component, Input, OnInit } from '@angular/core';
import { EnterpriseSearchService, ServiceType } from '@sabstravtech/obtservices/angular';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
  FlightEnterpriseSearchInterface,
  RailEnterpriseSearchInterface,
  EurostarEnterpriseSearchInterface,
  HotelEnterpriseSearchInterface,
  GetUserFavouriteSearchesResultArray,
  CarhireEnterpriseSearchInterface,
  LocationTypes,
  GetUserFavouriteSearchesResult,
  CabHireEnterpriseSearchInterface,
  FastTrackEnterpriseSearchInterface,
  ParkingEnterpriseSearchInterface,
  LoungesEnterpriseSearchInterface,
  IRLEnterpriseSearchInterface
} from '@sabstravtech/obtservices/base';
import { faTrash, faSpinner, faStar } from '@fortawesome/free-solid-svg-icons';
import { switchMap } from 'rxjs';

import { AirportExpressPipe } from '../../../startup/pipes/airport-express.pipe';
import { BaseComponent } from '../../../core/components/base_components/base-componet';

@Component({
  selector: 'app-favorite-items',
  templateUrl: './favorite-items.component.html',
  styleUrl: './favorite-items.component.scss',
  standalone: false
})
export class FavoriteItemsComponent extends BaseComponent implements OnInit {
  @Input() serviceType: ServiceType;
  private airportExpressPipe = new AirportExpressPipe();
  trash = faTrash;
  spinner = faSpinner;
  faStar = faStar;
  ServiceType = ServiceType;

  favouriteItems: any = {};
  dropdownOpen = false;
  isRemoving = false;

  serviceTypeIconClasses: Record<ServiceType, string> = {
    [ServiceType.Cab]: 'ico-taxi',
    [ServiceType.Car]: 'ico-car',
    [ServiceType.Flight]: 'ico-flight',
    [ServiceType.Hotel]: 'ico-bed',
    [ServiceType.FastTrack]: 'ico-material ico-fast-track',
    [ServiceType.InternationalRail]: 'ico-rail',
    [ServiceType.Lounge]: 'ico-material ico-lounge',
    [ServiceType.Parking]: 'ico-material ico-parking',
    [ServiceType.Rail]: 'ico-rail',
    [ServiceType.Eurostar]: 'custom_icon-eurostar',
    [ServiceType.Ferry]: 'ico-rail',
    [ServiceType.LongStays]: 'custom_icon-apartment',
    [ServiceType.SeasonTicket]: 'ico-material ico-season-ticket',
    [ServiceType.MeetingRoom]: 'ico-event',
    [ServiceType.Ambulance]: 'ico-ambulance ico-material'
  };

  serviceTypeNames: Record<ServiceType, string> = {
    [ServiceType.Cab]: this.translateService.instant('Taxi'),
    [ServiceType.Car]: this.translateService.instant('Car Hire'),
    [ServiceType.Flight]: this.translateService.instant('Flight'),
    [ServiceType.Hotel]: this.translateService.instant('Hotel'),
    [ServiceType.FastTrack]: this.translateService.instant('Fast Track'),
    [ServiceType.InternationalRail]: this.translateService.instant('International Rail'),
    [ServiceType.Lounge]: this.translateService.instant('Lounge'),
    [ServiceType.Parking]: this.translateService.instant('Parking'),
    [ServiceType.Rail]: this.translateService.instant('Rail'),
    [ServiceType.Eurostar]: this.translateService.instant('Eurostar'),
    [ServiceType.Ferry]: this.translateService.instant('Ferry'),
    [ServiceType.LongStays]: this.translateService.instant('Long Stays'),
    [ServiceType.SeasonTicket]: this.translateService.instant('Season Ticket'),
    [ServiceType.MeetingRoom]: this.translateService.instant('Meeting Room'),
    [ServiceType.Ambulance]: this.translateService.instant('Ambulance')
  };

  constructor(
    public searchService: EnterpriseSearchService,
    title: Title,
    public translateService: TranslateService
  ) {
    super(title, translateService);
  }

  ngOnInit() {
    this.subscribe(
      this.searchService.reloadFavourites.pipe(
        switchMap(() => this.searchService.getUserFavouriteSearches(this.serviceType))
      ),
      (data: GetUserFavouriteSearchesResultArray) => {
        this.favouriteItems = this.getFavouriteItems(data);
      },
      (error) => console.error('Error loading favourite items:', error)
    );
  }

  // Determine the title based on service type
  getTitle(item: GetUserFavouriteSearchesResult): string {
    switch (item.serviceType) {
      case ServiceType.Flight:
        return this.getFlightTitle(item);
      case ServiceType.Rail:
        return this.getRailTitle(item);
      case ServiceType.Eurostar:
        return this.getEurostarTitle(item);
      case ServiceType.Hotel:
        return this.getHotelTitle(item);
      case ServiceType.Car:
        return this.getCarTitle(item);
      case ServiceType.Lounge:
        return this.getLoungeTitle(item);
      case ServiceType.Parking:
        return this.getParkingTitle(item);
      case ServiceType.InternationalRail:
        return this.getRailTitle(item);
      case ServiceType.Cab:
        return this.getTaxiTitle(item);
      case ServiceType.FastTrack:
        return this.getFastTrackTitle(item);
      default:
        return this.translateService.instant('Unknown');
    }
  }

  private getFlightTitle(item: GetUserFavouriteSearchesResult): string {
    let title = '';

    // Check if it's a multi-city flight
    if (item.searchArgs?.multiDestination?.length) {
      title = this.translateService.instant('Multi-City');
      // Get the origin and destination from the multiDestination array
      const firstLeg = item.searchArgs.multiDestination[0];
      const lastLeg = item.searchArgs.multiDestination[item.searchArgs.multiDestination.length - 1];

      const origin = firstLeg.orig
        ? `${firstLeg.orig}` // Adjust if airportIata filter is needed here
        : 'Unknown';

      const destination = lastLeg.dest
        ? `${lastLeg.dest}` // Adjust if airportIata filter is needed here
        : 'Unknown';

      title = `${title}: ${origin} - ${destination}`;
    } else {
      const origin = item.searchArgs.originAirportFull
        ? `${item.searchArgs.originAirportFull} (${item.searchArgs.originAirport})`
        : item.searchArgs.originAirport || this.translateService.instant('Unknown');

      const destination = item.searchArgs.destinationAirportFull
        ? `${item.searchArgs.destinationAirportFull} (${item.searchArgs.destinationAirport})`
        : item.searchArgs.destinationAirport || this.translateService.instant('Unknown');

      title = `${origin} - ${destination}`;
    }

    return title;
  }

  private getRailTitle(item: GetUserFavouriteSearchesResult): string {
    if (item.searchArgs.isAirportExpress) {
      return `${this.airportExpressPipe.transform(item.searchArgs.airportExpressStation)} (${this.translateService.instant('Airport Express')})`;
    } else {
      const { travellingFrom, travellingTo } = item.searchArgs;
      const departureLocation = travellingFrom?.name;
      const arrivalLocation = travellingTo?.name;
      return `${departureLocation} - ${arrivalLocation}`;
    }
  }

  private getEurostarTitle(item: GetUserFavouriteSearchesResult): string {
    const {
      departLocation,
      arriveLocation,
      returnTrip,
      outboundCabinClassName,
      returnCabinClassName
    } = item.searchArgs;
    const departureLocation = departLocation?.destination;
    const arrivalLocation = arriveLocation?.destination;

    return `${departureLocation} - ${arrivalLocation}`;
  }

  private getHotelTitle(item: GetUserFavouriteSearchesResult): string {
    const {
      location_type_select,
      iata,
      airportDetail,
      cityName,
      office,
      trainStationCode,
      countryCode,
      postOrZipCode,
      trainsStationDetail
    } = item.searchArgs;
    let title = '';

    // Determine title based on location type
    switch (location_type_select) {
      case LocationTypes.Airport:
        const airportName = airportDetail?.destination
          ? `${airportDetail.destination} (${iata})`
          : iata || this.translateService.instant('Unknown');
        title = `${this.translateService.instant('Airport')}: ${airportName}`;
        break;
      case LocationTypes.City:
        title = cityName
          ? `${this.translateService.instant('City')}: ${cityName}`
          : postOrZipCode
            ? `${this.translateService.instant('City')}: ${postOrZipCode}`
            : this.translateService.instant('Unknown');
        break;
      case LocationTypes.Office:
        title = office?.name
          ? `${this.translateService.instant('Office')}: ${office.name}`
          : this.translateService.instant('Unknown');
        break;
      case LocationTypes.TrainStation:
        title = trainStationCode
          ? `${this.translateService.instant('Train Station')}: ${trainsStationDetail.name} (${trainStationCode})`
          : this.translateService.instant('Unknown');
        break;
      case LocationTypes.Shortlist:
        title = countryCode
          ? `${this.translateService.instant('Shortlist')}: ${countryCode}`
          : this.translateService.instant('Unknown');
        break;
      default:
        title = this.translateService.instant('Unknown');
    }
    return title;
  }

  private getCarTitle(item: GetUserFavouriteSearchesResult): string {
    let title = '';
    const { city, airport, depot } = item.searchArgs;
    if (city) {
      // Format for city pickup and dropoff
      const pickupCity = `${city.pickupVendorCode}, ${city.pickupCityName}, ${city.pickupAddressLines} (${city.pickupCountryCode})`;
      const dropoffCity = `${city.dropOffVendor}, ${city.dropOffCityName}, ${city.dropOffAddressLines} (${city.dropoffCountryCode})`;
      title = `${pickupCity} - ${dropoffCity}`;
    } else if (airport) {
      // Format for airport pickup and dropoff
      const pickupAirport = airport.pickupAirportFullName
        ? `${airport.pickupAirportFullName} (${airport.pickupCode})`
        : airport.pickupCode || this.translateService.instant('Unknown');
      const dropoffAirport = airport.dropoffAirportFullName
        ? `${airport.dropoffAirportFullName} (${airport.dropoffCode})`
        : airport.dropoffCode || this.translateService.instant('Unknown');
      title = `${pickupAirport} - ${dropoffAirport}`;
    } else if (depot && depot.pickupVendor) {
      // Format for depot pickup and dropoff
      const pickupDepot = `${depot.pickupVendor} ${depot.pickupAddressLines}, ${depot.pickupCityName}`;
      const dropoffDepot = `${depot.dropoffVendor} ${depot.dropoffAddressLines}, ${depot.dropoffCityName}`;
      title = `${pickupDepot} - ${dropoffDepot}`;
    } else {
      this.translateService.instant('Unknown');
    }
    return title;
  }

  private getLoungeTitle(item: GetUserFavouriteSearchesResult): string {
    return item.searchArgs.airportFullName;
  }

  private getFastTrackTitle(item: GetUserFavouriteSearchesResult): string {
    return item.searchArgs.airportFullName;
  }

  private getParkingTitle(item: GetUserFavouriteSearchesResult): string {
    return item.searchArgs.airportFullName;
  }

  private getTaxiTitle(item: GetUserFavouriteSearchesResult): string {
    const { fromLocation, toLocation } = item.searchArgs;

    const formatLocation = (location: any) => {
      const allSame =
        location.buildingName === location.streetAndNumber &&
        location.streetAndNumber === location.town;

      if (allSame) {
        return `${location.town} (${location.postcode})`;
      } else {
        return `${location.buildingName} ${location.streetAndNumber}, ${location.town} (${location.postcode})`;
      }
    };

    const fromText = formatLocation(fromLocation);
    const toText = formatLocation(toLocation);

    return `${fromText} ${this.translateService.instant(' to ')} ${toText}`;
  }

  favouritySelected(selectedItem: GetUserFavouriteSearchesResult) {
    this.searchService.isFavoritesSelected = false;
    console.log('Selected favorite:', selectedItem);
    Object.keys(this.searchService.search_objects).forEach((serviceType) => {
      if (serviceType === selectedItem.serviceType) {
        this.searchService.search_objects[serviceType].chosen = true;
      } else {
        this.searchService.search_objects[serviceType].chosen = false;
      }
    });
    this.searchService.determineHighestSearchPriority();
    switch (selectedItem.serviceType) {
      case ServiceType.Flight:
        const flightParams: FlightEnterpriseSearchInterface =
          this.searchService.searches[ServiceType.Flight];
        flightParams.applyFavourite(selectedItem.searchArgs);
        break;
      case ServiceType.Rail:
        const railParams: RailEnterpriseSearchInterface =
          this.searchService.searches[ServiceType.Rail];
        railParams.applyFavourite(selectedItem.searchArgs);
        console.log('Rail Search Params:', railParams);
        break;
      case ServiceType.Eurostar:
        const eurostarParams: EurostarEnterpriseSearchInterface =
          this.searchService.searches[ServiceType.Eurostar];
        eurostarParams.applyFavourite(selectedItem.searchArgs);
        console.log('Eurostar Search Params:', eurostarParams);
        break;
      case ServiceType.Hotel:
        const hotelParams: HotelEnterpriseSearchInterface =
          this.searchService.searches[ServiceType.Hotel];
        hotelParams.applyFavourite(selectedItem.searchArgs);
        break;
      case ServiceType.Car:
        const carParams: CarhireEnterpriseSearchInterface =
          this.searchService.searches[ServiceType.Car];
        carParams.applyFavourite(selectedItem.searchArgs);
        break;
      case ServiceType.Cab:
        const cabParams: CabHireEnterpriseSearchInterface =
          this.searchService.searches[ServiceType.Cab];
        cabParams.applyFavourite(selectedItem.searchArgs);
        break;
      case ServiceType.FastTrack:
        const fastTrackParams: FastTrackEnterpriseSearchInterface =
          this.searchService.searches[ServiceType.FastTrack];
        fastTrackParams.applyFavourite(selectedItem.searchArgs);
        break;
      case ServiceType.Parking:
        const parkingParams: ParkingEnterpriseSearchInterface =
          this.searchService.searches[ServiceType.Parking];
        parkingParams.applyFavourite(selectedItem.searchArgs);
        break;
      case ServiceType.Lounge:
        const loungeParams: LoungesEnterpriseSearchInterface =
          this.searchService.searches[ServiceType.Lounge];
        loungeParams.applyFavourite(selectedItem.searchArgs);
        break;
      case ServiceType.InternationalRail:
        const irlParams: IRLEnterpriseSearchInterface =
          this.searchService.searches[ServiceType.InternationalRail];
        irlParams.applyFavourite(selectedItem.searchArgs);
        break;
      default:
        return this.translateService.instant('Unknown');
    }
  }

  toggleFavouritesList() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  removeFavourite(item: GetUserFavouriteSearchesResult) {
    this.isRemoving = true;
    this.subscribe(this.searchService.deleteFavouriteSearchByID(item.id), () => {
      this.isRemoving = false;
      this.searchService.reloadFavourites.next();
    });
  }

  getFavouriteItems(data: GetUserFavouriteSearchesResultArray) {
    const favourites = {};
    data.forEach((favourite: GetUserFavouriteSearchesResult) => {
      if (!favourites[favourite.serviceType]) {
        favourites[favourite.serviceType] = [];
        favourites[favourite.serviceType].push(favourite);
      } else {
        favourites[favourite.serviceType].push(favourite);
      }
    });
    return favourites;
  }
}
